<template>
  <Modal isMaxLg showClose title="Twee factor authenticatie" @close="closePopup">
    <div class="auth-code-wrapper">
      <div v-if="state.displayAlert && alertMessage" class="alert alert-info alert-dismissible">
        <button type="button" class="close" @click="hideAlert" data-dismiss="alert"><span aria-hidden="true">×</span></button>
        {{ alertMessage }}
      </div>
      <label>Code invullen:</label>
      <AuthCodeInput
        :codeLength="6"
        :isError="!!state.errorMessage"
        @submit="send"
        v-model="state.TFAInput"
        ref="authCodeInputRef"
      />
      <span class="resend-code-wrapper">
        <span>Heeft u de code niet ontvangen?</span>
        <span v-if="!state.resendDelay" @click="reSendTwoFaToken" class="resend-code">Code opnieuw versturen</span>
        <span v-else>Probeer het na {{ state.resendDelay }} seconden opnieuw.</span>
      </span>
      <span class="error-message" v-if="state.errorMessage">{{ state.errorMessage }}</span>
    </div>

    <div class="actions">
      <Button type="primary" @click="send" :disabled="!state.TFAInput.every(input => input)">Doorgaan</Button>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import AuthCodeInput from "@/views/Common/AuthCodeInput.vue";
import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import request, {requestMethodType} from "@/plugins/request";
import { onBeforeMount, reactive, ref, computed } from "vue";
import {
  addNotification,
  getUserData,
  showGlobalError,
  showLoading,
  getTwoFaTokenMethod
} from "@/store/mainInterface";

defineOptions({
  inheritAttrs: false
});

const emit = defineEmits<{
  'submit': [token: string, code: string];
  'close': [];
}>();

const props = defineProps<{
  email?: string;
  isLogin?: boolean;
}>();

const state = reactive<{
  errorMessage: string;
  resendDelay: number;
  valid: boolean;
  TFAInput: string[];
  displayAlert: boolean;
  twoFaTokenMethod: string;
}>({
  errorMessage: null,
  resendDelay: 0,
  valid: false,
  TFAInput: [],
  displayAlert: true,
  twoFaTokenMethod: "",
});

const authCodeInputRef = ref<InstanceType<typeof AuthCodeInput>>(null);

const NewEmailCodeSentMessage = "Er is een nieuwe e-mail code verzonden.";
const NewSmsCodeSentMessage = "Er is een nieuwe SMS code verzonden.";
const NewEmailCodeStatus = "NewEmailCodeSent";
const NewSmsCodeStatus = "NewSmsCodeSent";
const alertMessage = computed(() => {
  if (state.twoFaTokenMethod === NewSmsCodeStatus) return NewSmsCodeSentMessage;
  if (state.twoFaTokenMethod === NewEmailCodeStatus) return NewEmailCodeSentMessage;
  return null;
});
async function send() {
  state.valid = false;
  state.errorMessage = null;
  state.twoFaTokenMethod = null;
  showGlobalError(false);
  emit('submit', state.TFAInput.join(''));
}

function setErrorMessage(errorMessage: string) {
  state.errorMessage = errorMessage;
}

async function reSendTwoFaToken() {
  showLoading(true);
  const response = await request('/api/tokens/resend-two-fa', requestMethodType.POST, { email: props.email }, { headers: {} });
  showLoading(false);

  const data = response.data;

  if (data) {
    state.twoFaTokenMethod = data.method;
    addNotification({ title: 'Code verzonden', message: 'Nieuwe code is verzonden' });
    state.TFAInput = [];

    state.resendDelay = 60;
    resendDelayCountdown();
  }
}

function hideAlert() {
  state.displayAlert = false;
}

function resendDelayCountdown() {
  if (state.resendDelay > 0) {
    setTimeout(() => {
      state.resendDelay--;
      resendDelayCountdown();
    }, 1000);
  }
}

function closePopup() {
  emit('close');
}

defineExpose({
  setErrorMessage,
});

onBeforeMount(async () => {
  if (props.isLogin) {
    state.twoFaTokenMethod = getTwoFaTokenMethod();
  } else {
    showLoading(true);
    const TFAResponse = await request('/api/tokens/send-two-fa', requestMethodType.POST, { 'email': props.email ?? getUserData().email }, { headers:{}, auth: true });
    showLoading(false);

    state.twoFaTokenMethod = TFAResponse.data.method;
  }
});
</script>

<style scoped lang="scss">
.title {
  text-align: center;
  font-weight: bold;
}

.auth-code-wrapper {
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
  width: fit-content;
  padding: 1.5rem 0;
}

.resend-code-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  font-size: 15px;

  .resend-code {
    cursor: pointer;
    margin-top: 10px;
    width: 100%;
    text-decoration-line: underline;
  }
}

.error-message {
  font-size: 10px;
  color: red;
}

.actions {
  display: flex;
  justify-content: center;
}

.auth-code-wrapper .auth-code_container {
  margin-top: 10px;
}

.close-button {
  cursor: pointer;
  float: right;
}

.alert {
  padding: 10px 40px 10px 10px;
  margin-bottom: 20px;
  -webkit-border-radius: 3px 3px 3px 3px;
  border-radius: 3px 3px 3px 3px;
  font-size: 13px;
  line-height: 18px;
  border: 1px solid transparent;
}
.alert-info {
  color: #31708f;
  background-color: #d9edf7;
  border-color: #bcdff1;
}
.close {
  float: right;
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 1;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  opacity: .2;
}
.alert-dismissible .close {
  position: relative;
  top: -2px;
  right: -21px;
  color: inherit;
}
button.close {
  -webkit-appearance: none;
  padding: 0;
  cursor: pointer;
  background: transparent;
  border: 0;
}
</style>
